import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaCalendar,
  FaUser,
  FaEye,
  FaClock,
  FaShare,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaWhatsapp,
  FaSpinner,
  FaLeaf,
  FaBookOpen,
  FaHeart,
  FaUtensils,
  FaPepperHot,
  FaTag,
} from "react-icons/fa";
import { blogAPI } from "../config/api";

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Animated fruit emojis for background
  const floatingFruits = [
    { emoji: "🍌", delay: 0, duration: 20, x: "10%", y: "15%" },
    { emoji: "🥭", delay: 2, duration: 25, x: "80%", y: "20%" },
    { emoji: "🍍", delay: 4, duration: 22, x: "15%", y: "70%" },
    { emoji: "🥥", delay: 1, duration: 23, x: "85%", y: "75%" },
    { emoji: "🍌", delay: 3, duration: 24, x: "50%", y: "10%" },
  ];

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("🔵 Fetching post with slug:", slug);

        // Fetch the blog post by slug
        const response = await blogAPI.getBySlug(slug);
        console.log("✅ Post data received:", response.data);

        setPost(response.data);

        // Fetch all posts to get related ones
        const allPostsResponse = await blogAPI.getAll();
        console.log("✅ All posts received:", allPostsResponse.data);

        const allPosts = Array.isArray(allPostsResponse.data)
          ? allPostsResponse.data
          : allPostsResponse.data.data || [];

        // Filter only published posts
        const publishedPosts = allPosts.filter(
          (p) => p.status === "published"
        );

        const related = publishedPosts
          .filter(
            (p) => p.slug !== slug && p.category === response.data.category
          )
          .slice(0, 3);

        console.log("✅ Related posts:", related);
        setRelatedPosts(related);

        // Scroll to top
        window.scrollTo(0, 0);
      } catch (err) {
        console.error("❌ Error fetching blog post:", err);
        console.error("❌ Error details:", err.response?.data);
        setError(
          err.response?.data?.message ||
            "Failed to load blog post. It may not exist or there was a server error."
        );
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Unknown date";
    }
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `https://worganaturals.com${imagePath}`;
  };

  // Format tags
  const formatTags = (tags) => {
    if (Array.isArray(tags)) {
      return tags.map((tag) => (typeof tag === "object" ? tag.name : tag));
    }
    if (typeof tags === "string" && tags.trim() !== "") {
      return tags.split(",").map((tag) => tag.trim());
    }
    return [];
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Sustainability: FaLeaf,
      "Health & Nutrition": FaHeart,
      Recipes: FaUtensils,
      Spices: FaPepperHot,
    };
    return icons[category] || FaBookOpen;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Sustainability:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      "Health & Nutrition":
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      Recipes:
        "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
      Spices:
        "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      default:
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
    };
    return colors[category] || colors.default;
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = post?.title || "";

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      shareUrl
    )}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
      shareUrl
    )}&title=${encodeURIComponent(shareTitle)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(
      shareTitle + " " + shareUrl
    )}`,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <FaSpinner className="animate-spin text-6xl text-green-600 dark:text-green-400 mx-auto mb-4" />
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Loading blog post...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center bg-white dark:bg-dark-700 p-8 rounded-2xl shadow-xl max-w-md"
        >
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Post Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || "The blog post you are looking for does not exist."}
          </p>
          <button
            onClick={() => navigate("/insights")}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-semibold"
          >
            Back to Insights
          </button>
        </motion.div>
      </div>
    );
  }

  const CategoryIcon = getCategoryIcon(post.category);
  const tagsArray = formatTags(post.tags);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden">
      {/* Animated Wave Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <svg
          className="absolute w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="wave-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                style={{ stopColor: "#10b981", stopOpacity: 0.2 }}
              />
              <stop
                offset="50%"
                style={{ stopColor: "#3b82f6", stopOpacity: 0.2 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: "#10b981", stopOpacity: 0.2 }}
              />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z"
            fill="url(#wave-gradient)"
            animate={{
              d: [
                "M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
                "M0,100 Q250,150 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
                "M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
              ],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </svg>
      </div>

      {/* Animated Fruit Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingFruits.map((fruit, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={{ left: fruit.x, top: fruit.y }}
            animate={{
              y: [0, -40, 0],
              x: [0, 20, 0],
              rotate: [0, 360],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: fruit.duration,
              repeat: Infinity,
              delay: fruit.delay,
              ease: "easeInOut",
            }}
          >
            <div className="text-6xl md:text-8xl opacity-20 drop-shadow-lg">
              {fruit.emoji}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Misty Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-white/30 dark:from-dark-900/50 dark:via-transparent dark:to-dark-900/30"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="pt-32 pb-12 px-6 md:px-12 lg:px-20"
        >
          <div className="container mx-auto max-w-5xl">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition mb-8 group"
            >
              <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Back to Blog</span>
            </Link>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-lg ${getCategoryColor(
                    post.category
                  )}`}
                >
                  <CategoryIcon />
                  {post.category}
                </span>
                {post.emoji && <span className="text-4xl">{post.emoji}</span>}
                {post.featured === 1 && (
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-400 text-yellow-900 shadow-lg">
                    ⭐ Featured
                  </span>
                )}
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                {post.title}
              </h1>

              <div className="flex flex-wrap gap-6 text-sm text-gray-600 dark:text-gray-400 mb-6">
                <div className="flex items-center gap-2">
                  <FaUser className="text-green-600 dark:text-green-400" />
                  <div>
                    <span className="font-medium">
                      {post.author || "Admin"}
                    </span>
                    {post.author_role && (
                      <span className="text-xs text-gray-500 dark:text-gray-500 ml-1">
                        ({post.author_role})
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FaCalendar className="text-green-600 dark:text-green-400" />
                  <span>
                    {formatDate(post.published_at || post.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FaEye className="text-green-600 dark:text-green-400" />
                  <span>{post.views || 0} views</span>
                </div>
                {post.read_time && (
                  <div className="flex items-center gap-2">
                    <FaClock className="text-green-600 dark:text-green-400" />
                    <span>{post.read_time}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {tagsArray.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  <FaTag className="text-gray-400 mt-1" />
                  {tagsArray.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Article Content */}
        <div className="px-6 md:px-12 lg:px-20 pb-20">
          <div className="container mx-auto max-w-5xl">
            <motion.article
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="backdrop-blur-sm bg-white/90 dark:bg-dark-700/90 rounded-3xl shadow-2xl overflow-hidden"
            >
              {/* Featured Image */}
              {post.image ? (
                <div className="relative h-96 md:h-[500px] overflow-hidden">
                  <img
                    src={getImageUrl(post.image)}
                    alt={post.image_alt || post.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error("Image failed to load:", post.image);
                      e.target.style.display = "none";
                      const parent = e.target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="w-full h-full bg-gradient-to-br from-green-400 to-blue-500 dark:from-green-600 dark:to-blue-700 flex items-center justify-center">
                            <svg class="text-white opacity-30" fill="currentColor" viewBox="0 0 20 20" style="width: 144px; height: 144px;">
                              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V4.804z"></path>
                            </svg>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="w-full h-96 md:h-[500px] bg-gradient-to-br from-green-400 to-blue-500 dark:from-green-600 dark:to-blue-700 flex items-center justify-center">
                  <FaLeaf className="text-white text-9xl opacity-30" />
                </div>
              )}

              {/* Content */}
              <div className="p-8 md:p-12">
                {/* Excerpt */}
                {post.excerpt && (
                  <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-600 dark:border-green-400 p-6 mb-8 rounded-r-xl">
                    <p className="text-lg text-gray-700 dark:text-gray-300 italic leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                )}

                {/* Main Content - Rich Text Display */}
                <div 
                  className="blog-content prose prose-lg dark:prose-invert max-w-none
                  prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-headings:mb-4 prose-headings:mt-8
                  prose-h1:text-4xl prose-h1:leading-tight
                  prose-h2:text-3xl prose-h2:leading-tight
                  prose-h3:text-2xl prose-h3:leading-snug
                  prose-h4:text-xl prose-h4:leading-snug
                  prose-h5:text-lg prose-h5:leading-normal
                  prose-h6:text-base prose-h6:leading-normal
                  prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6 prose-p:text-base
                  prose-a:text-green-600 dark:prose-a:text-green-400 prose-a:no-underline hover:prose-a:underline prose-a:font-medium prose-a:transition
                  prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-bold
                  prose-em:text-gray-700 dark:prose-em:text-gray-300 prose-em:italic
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:text-gray-700 dark:prose-ul:text-gray-300 prose-ul:my-6 prose-ul:space-y-2
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-gray-700 dark:prose-ol:text-gray-300 prose-ol:my-6 prose-ol:space-y-2
                  prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:leading-relaxed prose-li:mb-2
                  prose-blockquote:border-l-4 prose-blockquote:border-green-500 dark:prose-blockquote:border-green-400 
                  prose-blockquote:pl-6 prose-blockquote:pr-4 prose-blockquote:py-2 prose-blockquote:italic 
                  prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300 prose-blockquote:my-6
                  prose-blockquote:bg-green-50 dark:prose-blockquote:bg-green-900/20 prose-blockquote:rounded-r-lg
                  prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:text-green-600 dark:prose-code:text-green-400 
                  prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:font-semibold
                  prose-pre:bg-gray-100 dark:prose-pre:bg-gray-900 prose-pre:text-gray-800 dark:prose-pre:text-gray-200
                  prose-pre:p-6 prose-pre:rounded-xl prose-pre:overflow-x-auto prose-pre:my-6 prose-pre:shadow-inner
                  prose-pre:border prose-pre:border-gray-200 dark:prose-pre:border-gray-700
                  prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8 prose-img:w-full prose-img:h-auto
                  prose-hr:border-gray-300 dark:prose-hr:border-gray-600 prose-hr:my-8 prose-hr:border-t-2
                  prose-table:w-full prose-table:my-6 prose-table:border-collapse
                  prose-th:bg-green-100 dark:prose-th:bg-green-900/30 prose-th:text-gray-900 dark:prose-th:text-white 
                  prose-th:font-bold prose-th:p-3 prose-th:text-left prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600
                  prose-td:p-3 prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 
                  prose-td:text-gray-700 dark:prose-td:text-gray-300"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Meta Description */}
                {post.meta_description && (
                  <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                      📝 {post.meta_description}
                    </p>
                  </div>
                )}

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                      <FaShare className="text-green-600 dark:text-green-400" />
                      Share this post
                    </h3>
                    <div className="flex gap-3">
                      <a
                        href={shareLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition hover:scale-110 shadow-lg"
                        title="Share on Facebook"
                      >
                        <FaFacebook className="text-xl" />
                      </a>
                      <a
                        href={shareLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition hover:scale-110 shadow-lg"
                        title="Share on Twitter"
                      >
                        <FaTwitter className="text-xl" />
                      </a>
                      <a
                        href={shareLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition hover:scale-110 shadow-lg"
                        title="Share on LinkedIn"
                      >
                        <FaLinkedin className="text-xl" />
                      </a>
                      <a
                        href={shareLinks.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition hover:scale-110 shadow-lg"
                        title="Share on WhatsApp"
                      >
                        <FaWhatsapp className="text-xl" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <motion.section
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-16"
              >
                <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 flex items-center gap-3">
                  <FaBookOpen className="text-green-600 dark:text-green-400" />
                  Related Posts
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost, index) => (
                    <motion.div
                      key={relatedPost.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                    >
                      <Link
                        to={`/blog/${relatedPost.slug}`}
                        className="block bg-white dark:bg-dark-700 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                      >
                        <div className="h-48 bg-gradient-to-br from-green-400 to-blue-500 dark:from-green-600 dark:to-blue-700 relative overflow-hidden">
                          {relatedPost.image ? (
                            <img
                              src={getImageUrl(relatedPost.image)}
                              alt={relatedPost.image_alt || relatedPost.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FaLeaf className="text-white text-5xl opacity-50" />
                            </div>
                          )}
                          {relatedPost.category && (
                            <div className="absolute top-3 left-3">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold shadow-lg ${getCategoryColor(
                                  relatedPost.category
                                )}`}
                              >
                                {relatedPost.emoji} {relatedPost.category}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2 line-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition">
                            {relatedPost.title}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-4">
                            {relatedPost.excerpt ||
                              (relatedPost.content
                                ? relatedPost.content.replace(/<[^>]*>/g, '').substring(0, 100) + "..."
                                : "")}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <FaCalendar className="text-green-600 dark:text-green-400" />
                              {formatDate(
                                relatedPost.published_at || relatedPost.created_at
                              )}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaEye className="text-green-600 dark:text-green-400" />
                              {relatedPost.views || 0}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
